import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 * @property {HTMLButtonElement} [prevButton] - Previous media button.
 * @property {HTMLButtonElement} [nextButton] - Next media button.
 * @property {HTMLElement} [currentIndex] - Current index label.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());
  #activeIndex = 0;

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);
    this.#updateNavigation(0);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
  }

  /**
   * Opens the zoom dialog.
   *
   * ALTERAÇÃO v2 (correção do "flash" da imagem anterior): o diálogo
   * reabria na posição de scroll da abertura anterior e o
   * reposicionamento perdia contra scroll-snap / foco automático /
   * imagens lazy. Agora abre invisível e, durante os primeiros 400ms,
   * a posição é FIXADA a cada frame na imagem clicada, medida com
   * getBoundingClientRect em todos os contentores de scroll — só
   * depois do primeiro frame alinhado é que o diálogo aparece. Se o
   * utilizador interagir (toque, roda, tecla), a fixação para logo.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media } = this.refs;
    const targetImage = media[index];

    if (!targetImage) return;

    // Abrir escondido: visibility mantém o layout (os offsets funcionam)
    // mas nada é pintado até estarmos na posição certa.
    dialog.classList.add('dialog--preloading');
    dialog.style.visibility = 'hidden';
    dialog.showModal();

    this.#resetAllDragZooms();
    this.#loadHighResolutionImage(targetImage);
    this.#updateNavigation(index);
    this.selectThumbnail(index, { behavior: 'instant' });

    // ==========================================================
    // Fixação da posição: durante os primeiros 400ms, a cada
    // frame, o alvo é realinhado com o topo do(s) contentor(es)
    // de scroll — por medidas reais (getBoundingClientRect), o
    // que resiste a scroll-snap, ao foco automático do <dialog>,
    // ao scroll guardado da abertura anterior e às imagens lazy
    // que mudam o layout ao carregar. Se o utilizador tocar/
    // rodar a roda, a fixação para imediatamente.
    // ==========================================================
    const scrollContainers = this.#scrollAncestors(targetImage, dialog);
    const savedSnap = scrollContainers.map(function (el) {
      const v = el.style.scrollSnapType;
      el.style.scrollSnapType = 'none';
      return v;
    });
    const savedBehavior = scrollContainers.map(function (el) {
      const v = el.style.scrollBehavior;
      el.style.scrollBehavior = 'auto';
      return v;
    });

    let pinning = true;
    const stopPin = () => {
      if (!pinning) return;
      pinning = false;
      scrollContainers.forEach(function (el, i) {
        el.style.scrollSnapType = savedSnap[i];
        el.style.scrollBehavior = savedBehavior[i];
      });
    };

    ['wheel', 'touchstart', 'pointerdown', 'keydown'].forEach((type) => {
      dialog.addEventListener(type, stopPin, { once: true, passive: true });
    });

    const pinEnd = performance.now() + 400;
    const pin = () => {
      if (!pinning || !dialog.open) return stopPin();
      this.#alignToTarget(targetImage, scrollContainers);
      if (performance.now() < pinEnd) {
        requestAnimationFrame(pin);
      } else {
        stopPin();
      }
    };

    // 1.º alinhamento imediato, revelar no frame seguinte já alinhado
    this.#alignToTarget(targetImage, scrollContainers);
    requestAnimationFrame(() => {
      this.#alignToTarget(targetImage, scrollContainers);
      dialog.style.visibility = '';
      dialog.classList.remove('dialog--preloading');
      requestAnimationFrame(pin);
    });

    // As miniaturas continuam a acompanhar
    const targetThumbnail = this.refs.thumbnails.children[index];
    targetThumbnail?.scrollIntoView({ behavior: 'instant', block: 'nearest', inline: 'center' });
  }

  /**
   * Devolve os antepassados do alvo (até ao dialog, inclusive) que
   * conseguem fazer scroll.
   * @param {HTMLElement} target
   * @param {HTMLElement} dialog
   * @returns {HTMLElement[]}
   */
  #scrollAncestors(target, dialog) {
    const containers = [];
    let el = target.parentElement;
    while (el) {
      if (el.scrollHeight > el.clientHeight + 2 || el.scrollWidth > el.clientWidth + 2) {
        containers.push(el);
      }
      if (el === dialog) break;
      el = el.parentElement;
    }
    if (!containers.includes(dialog)) containers.push(dialog);
    return containers;
  }

  /**
   * Alinha o alvo com o topo/esquerda de cada contentor de scroll,
   * por medidas reais — funciona seja qual for o contentor que
   * realmente faz o scroll neste layout (desktop ou mobile).
   * @param {HTMLElement} target
   * @param {HTMLElement[]} containers
   */
  #alignToTarget(target, containers) {
    for (const container of containers) {
      const cr = container.getBoundingClientRect();
      const tr = target.getBoundingClientRect();
      if (container.scrollHeight > container.clientHeight + 2) {
        container.scrollTop += tr.top - cr.top;
      }
      if (container.scrollWidth > container.clientWidth + 2) {
        container.scrollLeft += tr.left - cr.left;
      }
    }
  }

  /**
   * Scrolls dialog/gallery to a media index without wrapping.
   * @param {number} index
   * @param {ScrollBehavior} behavior
   */
  #scrollToMedia(index, behavior = 'instant') {
    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];
    if (!targetImage) return;

    const gallery = dialog.querySelector('.dialog-zoomed-gallery');
    if (gallery instanceof HTMLElement) {
      gallery.scrollTo({
        left: targetImage.offsetLeft,
        top: 0,
        behavior,
      });
    }

    dialog.scrollTo({
      top: targetImage.offsetTop,
      left: 0,
      behavior,
    });

    targetImage.scrollIntoView({ behavior, block: 'start', inline: 'start' });
    targetThumbnail?.scrollIntoView({ behavior, block: 'nearest', inline: 'center' });
  }

  #resetAllDragZooms() {
    this.querySelectorAll('drag-zoom-wrapper').forEach((wrapper) => {
      if (wrapper instanceof HTMLElement && typeof /** @type {any} */ (wrapper).resetZoom === 'function') {
        /** @type {any} */ (wrapper).resetZoom();
      }
    });
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  #loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);

    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement) || activeIndex < 0) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === activeIndex}`);
    });

    this.#loadHighResolutionImage(mostVisibleElement);
    this.#updateNavigation(activeIndex);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const transitionName = `gallery-item`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', transitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * @param {KeyboardEvent} event
   */
  handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next();
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.previous();
    }
  }

  /**
   * @param {number} index
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * @param {number} index
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.#loadHighResolutionImage(media[index]);
  }

  previous() {
    if (this.#activeIndex <= 0) return;
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(this.#activeIndex - 1, { behavior });
  }

  next() {
    const lastIndex = this.refs.media.length - 1;
    if (this.#activeIndex >= lastIndex) return;
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(this.#activeIndex + 1, { behavior });
  }

  /**
   * @param {number} index
   */
  #updateNavigation(index) {
    const total = this.refs.media.length;
    const safeIndex = Math.max(0, Math.min(index, total - 1));
    this.#activeIndex = safeIndex;

    const { prevButton, nextButton, currentIndex } = this.refs;

    if (currentIndex) {
      currentIndex.textContent = String(safeIndex + 1);
    }

    if (prevButton) {
      prevButton.disabled = safeIndex <= 0;
    }

    if (nextButton) {
      nextButton.disabled = safeIndex >= total - 1;
    }
  }

  /**
   * @param {number} index
   * @param {{ behavior: ScrollBehavior }} options
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    const { media, thumbnails } = this.refs;
    if (isNaN(index) || index < 0 || index >= media.length) return;

    if (thumbnails?.children?.length) {
      const targetThumbnail = thumbnails.children[index];
      if (targetThumbnail instanceof HTMLElement) {
        Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
          button.setAttribute('aria-selected', `${i === index}`);
        });

        scrollIntoView(targetThumbnail, {
          ancestor: thumbnails,
          behavior: options.behavior,
          block: 'center',
          inline: 'center',
        });
      }
    }

    const targetImage = media[index];
    if (targetImage) {
      this.#scrollToMedia(index, options.behavior);
      this.#resetAllDragZooms();
      this.#loadHighResolutionImage(targetImage);
    }

    this.#updateNavigation(index);
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * @param {HTMLElement[]} elements
 * @returns {Promise<HTMLElement>}
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
